import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, CanLoad, Route, RouterStateSnapshot } from '@angular/router';

import { Observable, from, of, timer } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { AuthService } from './auth.service';

/**
 * AuthGuard - Route protection with retry logic for token refresh.
 * 
 * Key improvements:
 * 1. Retry mechanism: If first auth check fails, waits 500ms and retries once
 *    - This gives token refresh operations time to complete
 *    - Prevents immediate logout due to transient auth issues
 * 2. Graceful degradation: Falls back to logout only after retry fails
 * 3. No immediate logout: Attempts to resolve auth issues before giving up
 * 
 * This prevents users from being logged out unnecessarily when tokens are
 * being refreshed in the background.
 */
@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
   constructor(
      private auth: AuthService,
   ) { }

   canActivate( _, state: RouterStateSnapshot ): Observable<boolean> {
      return this.checkLogin( state.url );
   }

   canActivateChild( _, state: RouterStateSnapshot ): Observable<boolean> {
      return this.checkLogin( state.url );
   }

   canLoad( route: Route ): Observable<boolean> {
      return this.checkLogin( `/${route.path}` );
   }

   /**
    * Checks if user is authenticated with retry logic.
    * 
    * Strategy:
    * 1. First check: If logged in, allow access immediately
    * 2. If first check fails, wait 500ms and retry once
    *    - This delay allows background token refresh to complete
    *    - Handles cases where access token expired but refresh token is valid
    * 3. If retry also fails, then logout and deny access
    * 
    * @param url The URL being accessed
    * @returns Observable<boolean> - true if access allowed, false otherwise
    */
   checkLogin( _url: string ): Observable<boolean> {
      return this.auth.isLoggedIn$.pipe(
         switchMap( loggedIn => {
            if ( loggedIn ) { return of( true ); }

            // First check failed — retry after a short delay to give the
            // refresh-token exchange a second chance (e.g. transient network issue).
            return timer( 500 ).pipe(
               switchMap( () => this.auth.isLoggedIn$ ),
               switchMap( retryLoggedIn => {
                  if ( retryLoggedIn ) { return of( true ); }
                  // Both attempts failed - user is truly not authenticated
                  return from( this.auth.logout() ).pipe( map( () => false ));
               }),
            );
         }),
      );
   }
}
